import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { endSession, login as autenticar, restoreSession } from '@/modules/account/api';
import { setTokenReader } from '@/core/http/client';

/**
 * AUTENTICACAO
 *
 * O refresh token vive em cookie httpOnly + SameSite=Strict, emitido pelo
 * servico de identidade — inacessivel ao JavaScript. O access token, de vida
 * curta, fica apenas em memoria: nada de localStorage, que qualquer XSS le.
 * Ao recarregar a pagina, `/auth/session` troca o cookie por um access token
 * novo.
 */

export type AuthUser = {
  id: string;
  name: string;
  email: string;
  roles: string[];
  planSlug: string | null;
  advertiserKind: string | null;
  avatarUrl: string | null;
  /**
   * Conta criada mas e-mail ainda não confirmado. A pessoa ENTRA assim
   * mesmo — barrar o login de quem não confirmou é a forma mais rápida de
   * perder um cadastro que já foi feito. O que fica bloqueado é publicar
   * anúncio, e a tela avisa o motivo.
   */
  emailVerified: boolean;

  /**
   * Permissões efetivas, resolvidas pelo SERVIDOR a partir do perfil.
   *
   * Chegam prontas de propósito: se o cliente derivasse permissão do nome do
   * papel, a regra de composição existiria em dois lugares — e o lado que
   * não manda seria o mais fácil de alterar.
   *
   * Servem só para desenhar a tela. Toda chamada é reautorizada no servidor.
   */
  permissions: string[];
};

type AuthContextValue = {
  user: AuthUser | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  /** `remember` decide entre cookie de sessão e cookie persistente. */
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  /** Recarrega os dados do usuário — usado após confirmar e-mail ou assinar. */
  refreshUser: () => Promise<void>;
  hasRole: (...roles: string[]) => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

type SessionResponse = { accessToken: string; expiresInSeconds: number; user: AuthUser };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthContextValue['status']>('loading');
  const accessToken = useRef<string | null>(null);
  const queryClient = useQueryClient();
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setTokenReader(() => accessToken.current);
  }, []);

  const applySession = useCallback((session: SessionResponse) => {
    accessToken.current = session.accessToken;
    setUser(session.user);
    setStatus('authenticated');

    // Renova antes de expirar, com folga de 60s para latencia de rede.
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    const delay = Math.max(30, session.expiresInSeconds - 60) * 1000;
    refreshTimer.current = setTimeout(() => {
      void refresh();
    }, delay);
  }, []);

  const clearSession = useCallback(() => {
    accessToken.current = null;
    setUser(null);
    setStatus('anonymous');
    if (refreshTimer.current) clearTimeout(refreshTimer.current);

    /*
      LIMPA O CACHE DE CONSULTAS JUNTO COM A SESSAO.

      Sem isto, tudo que a pessoa anterior carregou continua na memoria desta
      aba: os contatos dela, os anuncios dela, a assinatura dela. Quem entrar
      em seguida no mesmo navegador ve esses dados pintados na tela enquanto a
      primeira requisicao da conta nova nao volta — dado de um em tela de
      outro, ainda que por um instante.

      `clear` e nao `invalidateQueries`: invalidar marca como velho e
      REAPROVEITA o valor antigo ate a nova resposta chegar, que e exatamente
      o que nao pode acontecer aqui.
    */
    queryClient.clear();
  }, [queryClient]);

  const refresh = useCallback(async () => {
    try {
      const session = await restoreSession();
      applySession(session);
    } catch {
      clearSession();
    }
  }, [applySession, clearSession]);

  useEffect(() => {
    /*
      Restaura a sessão em TODO ambiente, inclusive com mocks.

      Havia aqui um atalho que marcava "anônimo" direto quando os mocks
      estavam ligados — sobra da época em que o mock não tinha sessão. Com o
      mock implementando `/auth/session` de verdade, o atalho virou defeito:
      recarregar a página depois de entrar derrubava o usuário, e o painel
      administrativo ficava inalcançável por link direto.
    */
    void refresh();

    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string, remember = true) => {
      /*
        Passa pelo cliente de conta, e nao por um `request` proprio.

        A duplicacao custou caro: quando o login foi apontado para o servico de
        identidade, ESTA chamada continuou indo para o mock — a tela entrava
        com um token de faz-de-conta e o primeiro reload deslogava. Uma funcao
        so, num arquivo so, e o que impede a proxima migracao de deixar metade
        para tras.
      */
      const session = await autenticar(email, password, remember);
      applySession(session);
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    try {
      await endSession();
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      login,
      logout,
      refreshUser: refresh,
      hasRole: (...roles: string[]) => roles.some((role) => user?.roles.includes(role) ?? false),
    }),
    [user, status, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth precisa estar dentro de <AuthProvider>.');
  return context;
}

/** Papeis do usuario atual, ou lista vazia para visitante. */
export function useRoles(): string[] {
  return useAuth().user?.roles ?? [];
}
